import { TITLE_COLUMN_WIDTH_KEY } from '@couplespace/core/databases';

import type { DatabaseColumnDefinition } from './types';

const DEFAULT_TITLE_WIDTH = 160;
const DEFAULT_DATA_WIDTH = 120;

export const COLUMN_WIDTH_MIN = 48;
export const COLUMN_WIDTH_MAX = 800;

export function clampColumnWidth(width: number): number {
  return Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(width)));
}

export function sumTableColumnWidths(
  titleWidth: number,
  columnIds: string[],
  resolveWidth: (columnId: string) => number,
): number {
  return columnIds.reduce((sum, id) => sum + resolveWidth(id), titleWidth);
}

export function visibleColumns(
  columns: DatabaseColumnDefinition[],
  hiddenColumns?: string[],
): DatabaseColumnDefinition[] {
  if (!hiddenColumns?.length) {
    return columns;
  }
  const hidden = new Set(hiddenColumns);
  return columns.filter((c) => !hidden.has(c.id));
}

export function isColumnHidden(columnId: string, hiddenColumns?: string[]): boolean {
  return hiddenColumns?.includes(columnId) ?? false;
}

export function columnWidthPx(
  columnWidths: Record<string, number> | undefined,
  columnKey: string,
  fallback: number,
): number | undefined {
  const w = columnWidths?.[columnKey];
  return typeof w === 'number' ? w : fallback;
}

export function titleColumnWidth(columnWidths?: Record<string, number>): number {
  return columnWidthPx(columnWidths, TITLE_COLUMN_WIDTH_KEY, DEFAULT_TITLE_WIDTH) ?? DEFAULT_TITLE_WIDTH;
}

export function dataColumnWidth(
  columnId: string,
  columnWidths?: Record<string, number>,
): number {
  return columnWidthPx(columnWidths, columnId, DEFAULT_DATA_WIDTH) ?? DEFAULT_DATA_WIDTH;
}

export function toggleHiddenColumn(
  hiddenColumns: string[] | undefined,
  columnId: string,
  hide: boolean,
): string[] {
  const current = hiddenColumns ?? [];
  if (hide) {
    if (current.includes(columnId)) {
      return current;
    }
    return [...current, columnId];
  }
  return current.filter((id) => id !== columnId);
}
